import { memo, useMemo } from 'react';

import QuestionBox from '../QuestionBox';
import QuestionReply from '../QuestionReply';

import { EAnswerType, IQuestion } from '@src/types';

import styles from './questionThread.module.scss';
import { useRoomStore } from '@src/stores';
import { useQuestionReplies } from '@src/hooks';

interface IQuestionThreadProps {
  question: IQuestion;
  isAnswerSectionOpened: boolean;
  handleOpenAnswerBox: (answerType: EAnswerType) => void;
  handleToggleAnswerSection: () => void;
}

const QuestionThread = ({
  question,
  isAnswerSectionOpened,
  handleOpenAnswerBox,
  handleToggleAnswerSection,
}: IQuestionThreadProps) => {
  const fallbackAuthor = { name: 'Anonymous', id: 'test-id', recordId: '' };

  const { roomId } = useRoomStore();
  const { data: { question: apiQuestion } = {} } = useQuestionReplies(roomId, question.id, isAnswerSectionOpened);
  const questionWithReplies = useMemo(() => {
    return {
      ...question,
      replies: apiQuestion?.replies || question.replies,
    };
  }, [apiQuestion?.replies, question]);

  return (
    <div className={styles.threadContainer}>
      <QuestionBox
        question={questionWithReplies}
        isAnswerSectionOpened={isAnswerSectionOpened}
        onAnswersToggle={handleToggleAnswerSection}
        handleOpenAnswerBox={handleOpenAnswerBox}
      />
      {isAnswerSectionOpened && (
        <div className={styles.threadRepliesContainer}>
          {question.answer && (
            <QuestionReply
              answer={question.answer}
              author={question.assignTo || fallbackAuthor}
              isLastReply={!apiQuestion?.replies?.length}
            />
          )}
          {apiQuestion?.replies?.map((reply, idx, arr) => (
            <QuestionReply
              key={idx}
              author={reply.creator || fallbackAuthor}
              isLastReply={idx === arr.length - 1}
              answer={reply}
              initialAnswerType={question.answer?.answerType}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(QuestionThread);
